import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import CrewLineupEditor from './CrewLineupEditor';
import { BOAT_TYPES } from '../../../utils/boatTypes';

const CrewRowerSelectionStep = ({
  crews,
  availableRowers,
  selectedCrews,
  crewLineups,
  onCrewToggle,
  onLineupChange,
  onShowAllChange,
  onNext,
  showAll = false,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [showAdvancedView, setShowAdvancedView] = useState(false);
  // const [draggedRower, setDraggedRower] = useState(null); // This state seems unused

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (isMobile) {
    return (
      <div className="py-12 text-center">
        <div className="mb-4 text-6xl">📱</div>
        <h3 className="mb-2 text-lg font-medium text-gray-900">Desktop Required</h3>
        <p className="mx-auto max-w-md text-gray-600">
          The crew lineup interface requires a desktop or tablet for optimal drag-and-drop
          functionality. Please use a larger screen to schedule sessions with crew assignments.
        </p>
      </div>
    );
  }

  const handleLineupChange = (crewId, newLineup) => {
    console.log('=== LINEUP CHANGE ===');
    console.log('Crew ID:', crewId);
    console.log('New Lineup:', newLineup);

    const newLineups = { ...crewLineups };
    newLineups[crewId] = newLineup;

    console.log('Updated lineups:', newLineups);
    onLineupChange(newLineups);
  };

  const getAssignedRowers = () => {
    const assigned = new Set();
    Object.values(crewLineups).forEach((lineup) => {
      if (Array.isArray(lineup)) {
        lineup.forEach((rower) => {
          if (rower && rower.id) assigned.add(rower.id);
        });
      }
    });
    return assigned;
  };

  const getValidationStatus = () => {
    if (selectedCrews.length === 0) {
      return { isValid: false, message: 'Select at least one crew to continue' };
    }

    // Check experimental crews for complete lineups
    for (const crewId of selectedCrews) {
      const crew = crews.find((c) => c.id === crewId);
      if (crew && crew.is_experimental) {
        const boatType = BOAT_TYPES[crew.boat_type];
        const seatsCount = boatType?.seats || 0;
        const hasCox = boatType?.hasCox || false;
        const lineup = crewLineups[crewId] || [];
        const filledSeats = lineup.filter((rower) => rower && rower.role !== 'coxswain').length;
        const hasCoxswain = lineup.some((rower) => rower && rower.role === 'coxswain');

        if (filledSeats < seatsCount) {
          return {
            isValid: false,
            message: `${crew.name} needs ${seatsCount - filledSeats} more rower(s) assigned`,
          };
        }

        if (hasCox && !hasCoxswain) {
          console.warn(`${crew.name} requires a coxswain but none is assigned.`);
          // Optionally, you could set a state to display a warning message
        }
      }
    }

    return { isValid: true, message: '✓ Ready to proceed to coach selection' };
  };

  const assignedRowers = getAssignedRowers();
  const validationStatus = getValidationStatus();

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Select Crews & Assign Lineups</h3>
          <button
            onClick={() => setShowAdvancedView(!showAdvancedView)}
            className="rounded bg-gray-100 px-3 py-1 text-sm text-gray-700 transition-colors hover:bg-gray-200"
          >
            {showAdvancedView ? '👁️ Simple View' : '🔧 Advanced View'}
          </button>
        </div>
        <p className="text-sm text-gray-600">
          Choose which crews will participate and configure their lineups.
          {showAdvancedView && ' Advanced view shows validation details and optimization options.'}
        </p>
      </div>

      {/* Crews List */}
      <div className="space-y-4">
        {crews.map((crew) => {
          const isSelected = selectedCrews.includes(crew.id);
          const isExperimental = crew.is_experimental;
          const boatType = BOAT_TYPES[crew.boat_type];
          const seatsCount = boatType?.seats || 0;
          const hasCox = boatType?.hasCox || false;
          const totalPositions = seatsCount + (hasCox ? 1 : 0);
          const currentLineup = crewLineups[crew.id] || [];

          return (
            <div key={crew.id} className="rounded-lg border border-gray-200 p-4">
              <div className="mb-4 flex items-start justify-between">
                <label className="flex cursor-pointer items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onCrewToggle(crew.id)}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">{crew.name}</div>
                    <div className="text-sm text-gray-600">
                      {boatType?.name || crew.boat_type} •{' '}
                      {isExperimental ? 'Experimental' : 'Standard'} Crew
                      {crew.member_count && ` • ${crew.member_count} members`}
                    </div>
                  </div>
                </label>
              </div>

              {isSelected && (
                <div className="mt-4 border-t pt-4">
                  {isExperimental ? (
                    <div>
                      <h4 className="mb-3 text-sm font-medium text-gray-900">
                        Session Lineup Assignment
                      </h4>{' '}
                      <CrewLineupEditor
                        crew={crew}
                        availableRowers={availableRowers}
                        crewLineup={currentLineup}
                        onLineupChange={handleLineupChange}
                        assignedRowers={assignedRowers}
                        onShowAllChange={onShowAllChange}
                        showAll={showAll}
                      />
                    </div>
                  ) : (
                    <div>
                      <h4 className="mb-3 text-sm font-medium text-gray-900">
                        Standard Lineup (Auto-assigned)
                      </h4>
                      <div className="rounded-lg bg-gray-50 p-3">
                        <div className="space-y-2">
                          {crew.current_lineup && crew.current_lineup.length > 0 ? (
                            crew.current_lineup
                              .sort((a, b) => a.seat_number - b.seat_number)
                              .map((member, index) => {
                                // Find availability for this rower
                                const rowerAvailability = availableRowers.find(
                                  (r) => r.id === member.user_id
                                );
                                const availabilityStatus = rowerAvailability
                                  ? rowerAvailability.availability_status === 'fully_available'
                                    ? 'fully'
                                    : rowerAvailability.availability_status === 'partly_available'
                                      ? 'partly'
                                      : rowerAvailability.availability_status === 'already_assigned'
                                        ? 'assigned'
                                        : rowerAvailability.availability_status === 'not_available'
                                          ? 'not'
                                          : rowerAvailability.availability_status === 'conflict'
                                            ? 'conflict'
                                            : 'unknown'
                                  : 'unknown';

                                const statusColors = {
                                  fully: 'text-green-600 bg-green-50',
                                  partly: 'text-yellow-600 bg-yellow-50',
                                  not: 'text-red-600 bg-red-50',
                                  assigned: 'text-purple-600 bg-purple-50',
                                  conflict: 'text-red-700 bg-red-100 border-red-200',
                                  unknown: 'text-gray-600 bg-gray-50',
                                };

                                const statusLabels = {
                                  fully: 'Available',
                                  partly: 'Partially available',
                                  assigned: 'Already assigned to a session',
                                  not: 'Unavailable',
                                  conflict: 'Has conflicting session',
                                  unknown: 'Unknown',
                                };

                                return (
                                  <div key={member.user_id} className="rounded border bg-white p-2">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-3">
                                        {member.seat_number === 0 ? (
                                          <span className="w-12 text-sm font-medium text-gray-700">
                                            Cox
                                          </span>
                                        ) : (
                                          <span className="w-12 text-sm font-medium text-gray-700">
                                            Seat {member.seat_number}
                                          </span>
                                        )}
                                        <span className="text-sm font-medium text-gray-900">
                                          {member.first_name} {member.last_name}
                                        </span>
                                        {member.side !== 'none' ? (
                                          <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-500">
                                            {member.side}
                                          </span>
                                        ) : (
                                          <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-500">
                                            No preference
                                          </span>
                                        )}
                                      </div>
                                      <span
                                        className={`rounded-full px-2 py-1 text-xs font-medium ${statusColors[availabilityStatus]}`}
                                      >
                                        {statusLabels[availabilityStatus]}
                                      </span>
                                    </div>

                                    {/* Show conflict details for rowers with conflicting sessions */}
                                    {availabilityStatus === 'conflict' &&
                                      rowerAvailability?.availability_details?.conflicts && (
                                        <div className="mt-2 border-t border-red-100 pt-2">
                                          {rowerAvailability.availability_details.conflicts.map(
                                            (conflict, conflictIndex) => {
                                              // Format time without seconds
                                              const formatTime = (timeString) => {
                                                if (!timeString) return '';
                                                return timeString.substring(0, 5); // Remove seconds (HH:MM:SS -> HH:MM)
                                              };

                                              return (
                                                <div key={conflictIndex} className="text-xs">
                                                  <div className="font-medium text-red-700">
                                                    {conflict.session_name}
                                                  </div>
                                                  <div className="text-red-600">
                                                    {conflict.crew_name && (
                                                      <div className="mb-1">
                                                        <span className="font-medium">Crew:</span>{' '}
                                                        {conflict.crew_name}
                                                      </div>
                                                    )}
                                                    <div>
                                                      <span className="font-medium">Time:</span>{' '}
                                                      {formatTime(conflict.start_time)}-
                                                      {formatTime(conflict.end_time)}
                                                      {conflict.location &&
                                                        ` • ${conflict.location}`}
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            }
                                          )}
                                        </div>
                                      )}
                                  </div>
                                );
                              })
                          ) : (
                            <div className="p-2 text-sm text-gray-500">
                              No lineup assigned to this crew yet
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Enhanced Progress Indicator */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-medium text-blue-900">Step 1 of 3: Crew Configuration</h4>
          <div className="flex gap-1">
            <div className="h-2 w-8 rounded bg-blue-600"></div>
            <div className="h-2 w-8 rounded bg-gray-300"></div>
            <div className="h-2 w-8 rounded bg-gray-300"></div>
          </div>
        </div>
        <div className="space-y-1 text-sm text-blue-800">
          <div>• {selectedCrews.length} crew(s) selected</div>
          <div>• {assignedRowers.size} rower(s) assigned to experimental crews</div>
          {Object.entries(crewLineups).map(([crewId, lineup]) => {
            const crew = crews.find((c) => c.id === parseInt(crewId));
            if (!crew || !crew.is_experimental) return null;
            const boatType = BOAT_TYPES[crew.boat_type];
            const totalPositions = (boatType?.seats || 0) + (boatType?.hasCox ? 1 : 0);
            const filled = lineup.filter(Boolean).length;
            return (
              <div key={crewId} className="ml-4 flex items-center justify-between">
                <span>• {crew.name}:</span>
                <div className="flex items-center gap-2">
                  <span>
                    {filled}/{totalPositions} positions
                  </span>
                  {filled === totalPositions ? (
                    <span className="text-green-600">✓</span>
                  ) : filled > 0 ? (
                    <span className="text-amber-600">⚠️</span>
                  ) : (
                    <span className="text-gray-400">○</span>
                  )}
                </div>
              </div>
            );
          })}
          <div
            className={`border-t border-blue-200 pt-2 text-xs ${validationStatus.isValid ? 'text-blue-600' : 'text-red-600'}`}
          >
            {validationStatus.message}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={onNext}
          disabled={!validationStatus.isValid}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next: Select Coaches
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

CrewRowerSelectionStep.propTypes = {
  crews: PropTypes.array.isRequired,
  availableRowers: PropTypes.array.isRequired,
  selectedCrews: PropTypes.array.isRequired,
  crewLineups: PropTypes.object.isRequired,
  onCrewToggle: PropTypes.func.isRequired,
  onLineupChange: PropTypes.func.isRequired,
  onShowAllChange: PropTypes.func,
  onNext: PropTypes.func.isRequired,
  showAll: PropTypes.bool,
};

export default CrewRowerSelectionStep;
